'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useUnit } from 'effector-react';
import { AxiosError } from 'axios';
import { createWorkspaceNote } from '@nx-frontend/shared/api/webnotes';
import { useOpenPaywallPopup } from '@nx-frontend/shared/hooks/use-open-paywall-popup';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { AppError, NoteDto, PaywallPopupReason, PopupType } from '@nx-frontend/shared/types';

export const useCreateNote = () => {
  const openPaywallPopup = useOpenPaywallPopup();
  const [organization, orgInfo] = useUnit([$currentOrganization, $orgInfo]);
  const router = useRouter();

  /**
   * Create note and redirects to the created note
   * @param workspaceId
   * @param title
   * @param parentId
   * @param globalId
   * @param isPortalShare
   * @param redirectAfterCreate
   * @returns
   */
  return useCallback(
    async ({
      workspaceId,
      title,
      parentId,
      globalId,
      isPortalShare,
      redirectAfterCreate,
    }: {
      workspaceId: string;
      parentId: string;
      title?: string;
      globalId?: string;
      isPortalShare?: boolean;
      redirectAfterCreate?: boolean;
    }) => {
      let note: NoteDto | null = null;
      try {
        note = await createWorkspaceNote({
          workspaceId,
          noteName: title || 'New page',
          parentId,
          globalId,
          isPortalShare,
        });
      } catch (err) {
        let errorMessage = 'Fail to create note';
        let errorHandled = false;
        if (err instanceof AxiosError) {
          const error = err as AxiosError<AppError>;
          const errData = error.response?.data.error;
          if (typeof errData === 'object') {
            errorMessage = errData.message;
            if (errData.name === 'LimitError' && errData.objectType === 'NotesNumber') {
              // trigger displaying the paywall
              openPaywallPopup({
                payloadType: PopupType.PAYWALL,
                mode: 'upgrade',
                title: 'Get unlimited pages by upgrading your plan',
                reason: PaywallPopupReason.NOTES_LIMIT,
                orgId: organization.globalId,
                subscriptionType: orgInfo.subscriptionType,
              });
              errorHandled = true;
            }
          } else if (typeof errData === 'string') {
            errorMessage = errData;
          }
        }
        if (!errorHandled) {
          throw new Error(errorMessage);
        }
      }
      if (!note) {
        return;
      }

      if (redirectAfterCreate) {
        router.push('/space/' + workspaceId + '/page/' + note.globalId);
      }

      return note;
    },
    [openPaywallPopup, orgInfo.subscriptionType, organization.globalId, router]
  );
};
